import { AxiosInstance, CancelToken } from "axios"
import { OpfItem } from "src/types"
import { ApplicationContext } from "../api"

export const itemsEndpoints = (client: AxiosInstance) => {
  return {
    items: {
      ...items(client),
      ...drafts(client),
      ...configurator(client),
      ...files(client),
    },
  }
}

type ItemType = {
  applicationContext: ApplicationContext
  searchContext?: string
  opfItem: OpfItem
  opfItems?: any[]
  majorUpdate?: string
  opfId?: string
  opfLink?: string
  file?: any
  notification?: any
  approvalId?: string
  approvedBy?: string
  approvedDate?: string
  parentOpfId?: string
  moveOpfId?: string
  // variantId?: string
  slug?: string
  language?: string
  PrepublishDefaultLanguage?: string
  fromLanguage?: string
  toLanguage?: string
  versionId?: string
}

const opfItemDTO = (item?: OpfItem) => ({
  ...item,
  nextReviewDateIsNotApplicable: !item?.nextReviewDateIsApplicable,
})

const drafts = (client: AxiosInstance) => {
  return {
    createItemDraft: ({ applicationContext, opfItem }: ItemType) =>
      client.post("item-publishing/create-draft", {
        applicationContext,
        opfItem: opfItemDTO(opfItem),
      }),
    createItemTranslation: ({ applicationContext, opfItem }: ItemType) =>
      client.post("item-publishing/create-translation", {
        applicationContext,
        opfItem: opfItemDTO(opfItem),
      }),
    saveDraft: ({ applicationContext, opfItem }: ItemType) =>
      client.post("item-publishing/save-draft", {
        applicationContext,
        opfItem: opfItemDTO(opfItem),
      }),
    getLatestDraft: ({ applicationContext, opfItem }: ItemType) =>
      client.post("item-publishing/load-latest-version", { opfItem, applicationContext }),
  }
}

const configurator = (client: AxiosInstance) => {
  return {
    saveForConfigurator: ({ applicationContext, opfItems }: ItemType) =>
      client.post("opfitems/saveforconfigurator", { applicationContext, opfItems }),
    getItemsForConfigurator: (
      { applicationContext, opfItem }: ItemType,
      cancelToken: CancelToken
    ) =>
      client.post("opfitems/getforconfigurator", { applicationContext, opfItem }, { cancelToken }),
    removeItemFromConfigurator: ({ applicationContext, opfItem }: ItemType) =>
      client.post("opfitems/removeFromConfigurator", { applicationContext, opfItem }),
  }
}

const files = (client: AxiosInstance) => {
  return {
    deleteFile: ({ opfLink }: ItemType) => client.post("opfitems/deletefile", { opfLink }),
    uploadFile: ({ opfItem, file, applicationContext }: ItemType) => {
      const formData = new window.FormData()
      const headers = {
        "Content-Type": `multipart/form-data; boundary=--${opfItem.opfId}}`,
      }
      // const requestUrl = `opfitems/uploadfile?mediaFolder=${opfItem.opfId}&user=${applicationContext.user?.upn}&variantId=${opfItem.variantId}`
      const requestUrl = `opfitems/uploadfile?mediaFolder=${opfItem.opfId}&user=${applicationContext.user?.upn}`
      formData.append("file", file)
      return client.post(requestUrl, formData, { headers })
    },
  }
}

const items = (client: AxiosInstance) => {
  return {
    search: ({ applicationContext, searchContext }: ItemType) =>
      client.post("search/get", { applicationContext, searchContext }),
    getItems: ({ applicationContext, opfItem }: ItemType, cancelToken?: CancelToken) =>
      client.post("opfitems/get", { applicationContext, opfItem }, { cancelToken }),
    getItemBySlug: (
      {
        applicationContext,
        slug,
        level,
        language,
      }: {
        applicationContext: ApplicationContext
        slug: string
        level?: number
        language?: string
      },
      cancelToken?: CancelToken
    ) =>
      client.post(
        "opfitems/getbyslug",
        {
          applicationContext: language ? { ...applicationContext, language } : applicationContext,
          slug,
          depth: level,
        },
        { cancelToken }
      ),
    getAllNonLeafItems: ({ applicationContext, opfItem }: ItemType, cancelToken: CancelToken) =>
      client.post(
        "treestructure/getallnonleafitems",
        { applicationContext, opfItem },
        { cancelToken }
      ),
    getLanguagesForItem: ({ applicationContext, opfItem }: ItemType) =>
      client.post("opfitems/getlanguagesforitem", { applicationContext, opfItem }),
    getVersionForItemLanguage: ({ applicationContext, slug, language }: ItemType) =>
      client.post("item-publishing/load-versions-for-compare", {
        applicationContext,
        slug,
        language,
      }),
    prePublishMinorVersion: ({
      applicationContext,
      opfItem,
      opfId,
      notification,
      PrepublishDefaultLanguage,
    }: ItemType) =>
      client.post("item-publishing/pre-publish-minor-version", {
        applicationContext,
        opfId,
        notification: { ...notification, isMajorChange: false },
        PrepublishDefaultLanguage,
        opfItem: {
          ...opfItemDTO(opfItem),
          notification: { ...opfItem.notification, isMajorChange: false },
        },
      }),
    prePublishMajorVersion: ({
      applicationContext,
      opfItem,
      opfId,
      notification,
      PrepublishDefaultLanguage,
    }: ItemType) =>
      client.post("item-publishing/pre-publish-major-version", {
        applicationContext,
        opfId,
        notification: { ...notification, isMajorChange: true },
        PrepublishDefaultLanguage,
        opfItem: {
          ...opfItemDTO(opfItem),
          notification: { ...opfItem.notification, isMajorChange: true },
        },
      }),
    publishMinorVersion: ({
      applicationContext,
      opfItem,
      opfId,
      notification,
      PrepublishDefaultLanguage,
      approvalId,
      approvedBy,
      approvedDate,
    }: ItemType) =>
      client.post("item-publishing/publish-minor-version", {
        applicationContext,
        opfId,
        notification: { ...notification, isMajorChange: true },
        PrepublishDefaultLanguage,
        opfItem: {
          ...opfItemDTO(opfItem),
          notification: { ...opfItem.notification, isMajorChange: true },
        },
        approvalId,
        approvedBy,
        approvedDate,
      }),
    publishMajorVersion: ({
      applicationContext,
      opfItem,
      opfId,
      notification,
      PrepublishDefaultLanguage,
      approvalId,
      approvedBy,
      approvedDate,
    }: ItemType) =>
      client.post("item-publishing/publish-major-version", {
        applicationContext,
        opfId,
        notification: { ...notification, isMajorChange: true },
        PrepublishDefaultLanguage,
        opfItem: {
          ...opfItemDTO(opfItem),
          notification: { ...opfItem.notification, isMajorChange: true },
        },
        approvalId,
        approvedBy,
        approvedDate,
      }),
    publishItem: ({ applicationContext, opfId, approvalId, language }: ItemType) =>
      client.post("item-publishing/publish-version", {
        applicationContext: {
          ...applicationContext,
          language: language || applicationContext.language,
        },
        opfId,
        approvalId,
      }),
    updateItem: ({ applicationContext, opfItem, majorUpdate }: ItemType) =>
      client.post("opfitems/update", {
        applicationContext,
        opfItem: opfItemDTO(opfItem),
        majorUpdate,
      }),
    archiveItem: ({
      applicationContext,
      opfItem,
      approvalId,
      approvedBy,
      approvedDate,
    }: ItemType) => {
      return client.post("opfitems/archive", {
        applicationContext,
        opfItem: {
          ...opfItem,
          approvalId,
          approvedBy,
          approvedDate,
          opfVersionId: opfItem.opfVersionId ?? opfItem.id,
        },
      })
    },
    createItem: ({ applicationContext, opfItem, majorUpdate }: ItemType) =>
      client.post("opfitems/create", {
        applicationContext,
        opfItem: opfItemDTO(opfItem),
        majorUpdate,
      }),

    autoTranslateItem: ({ applicationContext, opfItem, ...params }: ItemType) =>
      client.post("item-publishing/load-version-auto-translation", {
        applicationContext,
        opfItem,
        ...params,
      }),
    autoTranslatedItemForLanguage: ({
      applicationContext,
      opfItem,
      fromLanguage,
      toLanguage,
      ...params
    }: ItemType) =>
      client.post(
        `item-publishing/load-version-auto-translation-for-languages?fromLanguage=${fromLanguage}&toLanguage=${toLanguage}`,
        {
          applicationContext,
          opfItem,
          ...params,
        }
      ),
    autoTranslatedFirstItemForLanguage: ({ applicationContext, opfItem, toLanguage }: ItemType) =>
      client.post(
        `item-publishing/load-first-version-auto-translation-for-languages?toLanguage=${toLanguage}`,
        {
          applicationContext,
          opfItem,
          toLanguage,
        }
      ),
    autoTranslatedItemStep: ({ applicationContext, opfItem, language }: ItemType) =>
      client.post("item-publishing/load-step-details-auto-translation", {
        applicationContext: {
          ...applicationContext,
          language: language || applicationContext.language,
        },
        editDocumentSubDetailDto: editDocumentSubDetailsDataTransferObject(opfItem),
      }),

    fetchDocumentVersions: ({ applicationContext, opfId }: ItemType) =>
      client.post("opfitems/getVersionsByOpfId", {
        applicationContext,
        opfId,
      }),
    fetchDocumentVersionById: ({ applicationContext, opfId, versionId }: ItemType) =>
      client.post("opfitems/getVersionById", {
        applicationContext,
        opfId,
        id: versionId,
      }),

    createItemFromWord: ({ applicationContext, file, parentOpfId }: ItemType) => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("applicationContextJson", JSON.stringify(applicationContext))
      formData.append("parentOpfId", parentOpfId ?? "")
      return client.post("opfitems/import-word-document", formData, {
        headers: { "Content-Type": "multipart/form-data;" },
      })
    },
    // createItemVariant: ({ applicationContext, opfItem, majorUpdate }: ItemType) =>
    //   client.post("opfitems/createVariant", {
    //     applicationContext,
    //     opfItem: opfItemDTO(opfItem),
    //     majorUpdate,
    //   }),
    lockItem: ({ applicationContext, opfItem }: ItemType) =>
      client.post("opfitems/lock", { applicationContext, opfItem }),
    unlockItem: ({ applicationContext, opfItem }: ItemType) =>
      client.post("opfitems/unlock", { applicationContext, opfItem }),
    translateItem: ({ applicationContext, opfItem }: ItemType) =>
      client.post("opfitems/translate", { applicationContext, opfItem: opfItemDTO(opfItem) }),
    reorder: ({ applicationContext, opfItems }: ItemType) =>
      client.post("opfitems/order", { applicationContext, opfItems }),
    moveItem: ({ applicationContext, opfItem, moveOpfId }: ItemType) =>
      client.post("opfitems/move", { applicationContext, opfItem, moveOpfId }),
    getIcons: () => client.get("opfitems/geticons"),
    fetchDocumentChangelog: ({ applicationContext, opfId, language }: ItemType) =>
      client.post("changelog/read-item-changelog", {
        applicationContext,
        opfId,
        language,
      }),
    subscribeToNotification: ({
      applicationContext,
      opfId,
      slug,
      language,
    }: {
      applicationContext: ApplicationContext
      opfId: string
      slug: string
      language: string
    }) =>
      client.post("opfitems/subscribe", {
        applicationContext,
        opfId: opfId,
        slug: slug,
        language: language,
      }),
    unsubscribeFromNotification: ({
      applicationContext,
      opfId,
      slug,
      language,
    }: {
      applicationContext: ApplicationContext
      opfId: string
      slug: string
      language: string
    }) =>
      client.post("opfitems/unsubscribe", {
        applicationContext,
        opfId: opfId,
        slug: slug,
        language: language,
      }),
  }
}

const editDocumentSubDetailsDataTransferObject = (opfItem?: OpfItem) => ({
  title: opfItem?.title,
  text: opfItem?.text,
  language: opfItem?.language,
})
